import React from 'react';
import { CheckCircle, AlertTriangle, RefreshCw, ShieldAlert, Ban, Copy } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { api } from '../../App';
export const muted = 'text-[hsl(var(--muted-foreground))]';
export const fmt = iso => iso ? new Date(iso).toLocaleString('en-GB', {
  dateStyle: 'medium',
  timeStyle: 'short'
}) : '—';

// Amazon SES delivery card (Settings tab). Credentials live only in server environment variables and are
// never shown here; this panel only reads status, adjusts safety controls and sends a single test email.
export const SesReadiness = ({
  acct,
  configured,
  connOk,
  isSes,
  st
}) => <div className="p-3 rounded-lg border border-[hsl(var(--border))] text-xs space-y-1" data-testid="ses-readiness">
          <Label className="font-semibold">Go-live readiness</Label>
          {[['Environment variables present', configured], ['AWS connection check passed', connOk], ['Production access (not sandbox)', connOk && acct.production_access === true], ['Successful SES test email', !!st.test_passed_at], ['SNS feedback topic approved & confirmed', !!st.sns?.confirmed_at], ['Live provider switched to Amazon SES (only after explicit approval)', isSes], ['Live sending switched on per type (each after explicit approval)', !!(st.live?.transactional || st.live?.newsletters || st.live?.migration)]].map(([label, ok]) => <div key={label} className="flex items-center gap-2" data-testid="ses-readiness-item">
              {ok ? <CheckCircle className="w-3.5 h-3.5 text-green-600 shrink-0" /> : <span className="w-3.5 h-3.5 rounded-full border border-[hsl(var(--border))] inline-block shrink-0" />}
              <span className={ok ? '' : muted}>{label}</span>
            </div>)}
        </div>;
export const SesSwitchDialog = ({
  busy,
  setSwitchDialog,
  setTick,
  switchDialog,
  switchProvider,
  tick
}) => <DialogContent data-testid="ses-switch-dialog">
          <DialogHeader>
            <DialogTitle>{switchDialog === 'ses' ? 'Make Amazon SES the live provider?' : 'Switch back to own mailbox (SMTP)?'}</DialogTitle>
            <DialogDescription>
              {switchDialog === 'ses' ? 'From now on every queued email (tickets, membership, migration invitations, newsletters) is sent through Amazon SES. SMTP will not be used at all.' : 'Every queued email will be sent through the own-mailbox SMTP settings again. Amazon SES will not be used at all.'}
            </DialogDescription>
          </DialogHeader>
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input type="checkbox" className="mt-1" checked={tick} onChange={e => setTick(e.target.checked)} data-testid="ses-switch-confirm-checkbox" />
            <span>I understand only one provider sends email and there is no automatic fallback.</span>
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
      setSwitchDialog(null);
      setTick(false);
    }}>Cancel</Button>
            <Button onClick={switchProvider} disabled={!tick || busy} data-testid="ses-switch-submit-button">Confirm switch</Button>
          </DialogFooter>
        </DialogContent>;
export const SesGateDialog = ({
  busy,
  gateDialog,
  gateTick,
  setGateDialog,
  setGateTick,
  toggleGate
}) => <DialogContent data-testid="ses-gate-dialog">
          <DialogHeader>
            <DialogTitle>{gateDialog?.on ? `Switch ON live sending: ${gateDialog?.label}?` : `Switch OFF live sending: ${gateDialog?.label}?`}</DialogTitle>
            <DialogDescription>
              {gateDialog?.on ? 'Emails of this type that are waiting in the queue will start sending through Amazon SES straight away, followed by new ones. Nothing already sent is repeated.' : 'New and waiting emails of this type will be held in the queue until switched on again. Nothing is lost.'}
            </DialogDescription>
          </DialogHeader>
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input type="checkbox" className="mt-1" checked={gateTick} onChange={e => setGateTick(e.target.checked)} data-testid="ses-gate-confirm-checkbox" />
            <span>{gateDialog?.on ? 'This activation has been explicitly approved.' : 'I understand these emails will wait in the queue.'}</span>
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
      setGateDialog(null);
      setGateTick(false);
    }}>Cancel</Button>
            <Button onClick={toggleGate} disabled={!gateTick || busy} data-testid="ses-gate-submit-button">Confirm</Button>
          </DialogFooter>
        </DialogContent>;
export const SesConfirmDialog377 = ({
  busy,
  setSwitchDialog,
  setTick,
  switchDialog,
  switchProvider,
  tick
}) => <Dialog open={!!switchDialog} onOpenChange={o => {
  if (!o) {
    setSwitchDialog(null);
    setTick(false);
  }
}}>
        <SesSwitchDialog busy={busy} setSwitchDialog={setSwitchDialog} setTick={setTick} switchDialog={switchDialog} switchProvider={switchProvider} tick={tick} />
      </Dialog>;
export const SesConfirmDialog397 = ({
  busy,
  gateDialog,
  gateTick,
  setGateDialog,
  setGateTick,
  toggleGate
}) => <Dialog open={!!gateDialog} onOpenChange={o => {
  if (!o) {
    setGateDialog(null);
    setGateTick(false);
  }
}}>
        <SesGateDialog busy={busy} gateDialog={gateDialog} gateTick={gateTick} setGateDialog={setGateDialog} setGateTick={setGateTick} toggleGate={toggleGate} />
      </Dialog>;
export const SesConnectionStatus = ({
  acct,
  busy,
  configured,
  connOk,
  load
}) => <div className="flex items-start gap-3 p-3 rounded-lg bg-[hsl(var(--muted)/0.5)]" data-testid="ses-connection-status">
          {connOk ? <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" /> : <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />}
          <div className="flex-1 text-sm">
            {!configured && <>
                <p className="font-medium">Not configured yet — nothing is sent</p>
                <p className={`text-xs ${muted}`}>
                  Add these server environment variables through the secure settings: <code>{(acct.missing || []).join(', ')}</code>.
                  Emails routed to SES are held safely in the queue until then. No other provider is used as a fallback.
                </p>
              </>}
            {configured && connOk && <>
                <p className="font-medium">Connected — {acct.region} · sender {acct.from_name} &lt;{acct.from_email}&gt;</p>
                <p className={`text-xs ${muted}`}>
                  {acct.production_access ? 'Production access' : 'Sandbox (verified recipients only)'} · AWS allows {acct.max_24_hour_send?.toLocaleString?.() ?? '—'} / 24h at {acct.max_send_rate ?? '—'}/s
                  · AWS used last 24h: {acct.sent_last_24_hours ?? '—'}
                </p>
              </>}
            {configured && !connOk && <>
                <p className="font-medium">Configured, but AWS refused the connection ({acct.error || 'error'})</p>
                <p className={`text-xs ${muted}`}>Check the IAM key, its permissions (ses:SendRawEmail, ses:GetAccount) and the region. Queued emails are unaffected.</p>
              </>}
          </div>
          <Button variant="ghost" size="sm" onClick={() => load(true)} disabled={busy} data-testid="ses-refresh-button" aria-label="Refresh status"><RefreshCw className="w-4 h-4" /></Button>
        </div>;
export const SesLiveGates = ({
  GATES,
  busy,
  isSes,
  q,
  setGateDialog,
  st
}) => <div className="p-3 rounded-lg border border-[hsl(var(--border))] space-y-2" data-testid="ses-live-gates">
              <Label className="font-semibold flex items-center gap-2"><ShieldAlert className="w-4 h-4" /> Live sending — per type</Label>
              <p className={`text-xs ${muted}`}>
                Every type starts OFF. While a type is OFF its emails wait safely in the queue (currently held: <strong data-testid="ses-held-by-gate">{st.live?.held_by_gate ?? 0}</strong>) and nothing is attempted, even with Amazon SES selected.
                Switch a type on only after it has been approved.
              </p>
              <div className="grid sm:grid-cols-3 gap-2">
                {GATES.map(([key, label, desc]) => {
      const on = !!st.live?.[key];
      return <div key={key} className={`p-2.5 rounded-md border text-xs space-y-1 ${on ? 'border-green-600/60 bg-green-500/5' : 'border-[hsl(var(--border))]'}`} data-testid={`ses-gate-${key}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium">{label}</span>
                        <Badge variant={on ? 'default' : 'outline'} data-testid={`ses-gate-${key}-badge`}>{on ? 'ON' : 'OFF'}</Badge>
                      </div>
                      <p className={muted}>{desc}</p>
                      <Button size="sm" variant={on ? 'outline' : 'default'} disabled={busy || !on && !isSes} onClick={() => setGateDialog({
          key,
          label,
          on: !on
        })} data-testid={`ses-gate-${key}-button`} title={!on && !isSes ? 'Make Amazon SES the live provider first' : ''}>
                        {on ? 'Switch OFF' : 'Switch ON'}
                      </Button>
                    </div>;
    })}
              </div>
              {q.campaign_auto_paused_reason && <p className="text-xs text-amber-700" data-testid="ses-auto-pause-reason">Automatic pause: {q.campaign_auto_paused_reason}</p>}
            </div>;
export const SesControlsSection = ({
  busy,
  controls,
  saveControls,
  setControls,
  st
}) => <div className="p-3 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted)/0.3)] space-y-3" data-testid="ses-controls-section">
              <div>
                <Label className="font-semibold">Internal safety controls</Label>
                <p className={`text-xs ${muted}`}>These are our own caps, kept below the AWS account limits (currently {st.limits_reference?.ses_production_daily?.toLocaleString?.()} / day, {st.limits_reference?.ses_production_rate}/s — editable below). Temporary failures retry with doubling delays, then wait for review.</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div><Label className="text-xs">Max per second</Label><Input type="number" min="1" max={controls.ses_aws_rate_limit} value={controls.ses_max_rate_per_sec} onChange={e => setControls({
        ...controls,
        ses_max_rate_per_sec: e.target.value
      })} data-testid="ses-rate-input" /></div>
                <div><Label className="text-xs">Daily safety limit</Label><Input type="number" min="1" max={controls.ses_aws_daily_limit} value={controls.ses_daily_safety_limit} onChange={e => setControls({
        ...controls,
        ses_daily_safety_limit: e.target.value
      })} data-testid="ses-daily-input" /></div>
                <div><Label className="text-xs">Retries</Label><Input type="number" min="0" max="10" value={controls.ses_max_retries} onChange={e => setControls({
        ...controls,
        ses_max_retries: e.target.value
      })} data-testid="ses-retries-input" /></div>
                <div><Label className="text-xs">First retry delay (s)</Label><Input type="number" min="30" max="3600" value={controls.ses_retry_base_seconds} onChange={e => setControls({
        ...controls,
        ses_retry_base_seconds: e.target.value
      })} data-testid="ses-retry-delay-input" /></div>
              </div>
              <div>
                <Label className="text-xs font-semibold">AWS account limits (as granted by Amazon)</Label>
                <p className={`text-xs ${muted}`}>Update these when AWS raises your quota; they set the upper bound for the caps above.</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div><Label className="text-xs">AWS max per second</Label><Input type="number" min="1" max="1000" value={controls.ses_aws_rate_limit} onChange={e => setControls({
        ...controls,
        ses_aws_rate_limit: e.target.value
      })} data-testid="ses-aws-rate-input" /></div>
                <div><Label className="text-xs">AWS daily quota</Label><Input type="number" min="1" max="10000000" value={controls.ses_aws_daily_limit} onChange={e => setControls({
        ...controls,
        ses_aws_daily_limit: e.target.value
      })} data-testid="ses-aws-daily-input" /></div>
              </div>
              <Button onClick={saveControls} disabled={busy} variant="outline" size="sm" data-testid="ses-controls-save-button">Save controls</Button>
            </div>;
export const SesSuppressionSection = ({
  busy,
  loadSuppressions,
  run,
  setSuppQuery,
  setSuppressEmail,
  suppList,
  suppQuery,
  suppressEmail,
  token
}) => <div className="p-3 rounded-lg border border-[hsl(var(--border))] space-y-2" data-testid="ses-suppression-section">
              <Label className="font-semibold flex items-center gap-2"><Ban className="w-4 h-4" /> Block an address from all email</Label>
              <p className={`text-xs ${muted}`}>Hard bounces and complaints are added automatically. Use this when someone asks us to stop emailing them entirely.</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input type="email" value={suppressEmail} onChange={e => setSuppressEmail(e.target.value)} placeholder="email@example.com" className="sm:w-72" data-testid="ses-suppress-input" />
                <Button variant="outline" size="sm" disabled={busy || !suppressEmail.includes('@')} onClick={() => run(() => api.postAuth('/api/admin/email/suppressions', {
      email: suppressEmail.trim(),
      note: 'Added from Settings'
    }, token)).then(() => {
      setSuppressEmail('');
      loadSuppressions(suppQuery);
    })} data-testid="ses-suppress-add-button">Block address</Button>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 pt-1">
                <Input value={suppQuery} onChange={e => setSuppQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && loadSuppressions(suppQuery)} placeholder="Search suppressed addresses" className="sm:w-72" data-testid="ses-suppress-search-input" />
                <Button variant="ghost" size="sm" onClick={() => loadSuppressions(suppQuery)} data-testid="ses-suppress-search-button"><RefreshCw className="w-4 h-4 mr-1" /> Search</Button>
              </div>
              {suppList && <div className="text-xs space-y-1" data-testid="ses-suppress-list">
                  <p className={muted}>{suppList.total} address(es) on the list{suppList.total > 50 ? ' — showing the first 50' : ''}</p>
                  {suppList.items.map(x => <div key={x.email} className="flex flex-wrap items-center gap-2 py-1 border-t border-[hsl(var(--border))]" data-testid="ses-suppress-row">
                      <span className="font-mono">{x.email}</span>
                      <Badge variant="outline">{String(x.reason || '').replace(/_/g, ' ')}</Badge>
                      <span className={muted}>{x.source}{x.note ? ` · ${x.note}` : ''} · {fmt(x.created_at)}</span>
                      <Button size="sm" variant="ghost" className="ml-auto" disabled={busy} onClick={() => run(() => api.deleteAuth(`/api/admin/email/suppressions/${encodeURIComponent(x.email)}`, token), 'Address removed from the list').then(() => loadSuppressions(suppQuery))} data-testid="ses-suppress-remove-button">Remove</Button>
                    </div>)}
                  {!suppList.items.length && <p className={muted}>No suppressed addresses. Hard bounces and complaints will appear here automatically once SNS feedback is connected.</p>}
                </div>}
            </div>;
export const SesFeedbackSection = ({
  busy,
  cfgSet,
  checkEndpoint,
  copyEndpoint,
  epCheck,
  run,
  saveSnsConfig,
  setCfgSet,
  setTopicArn,
  st,
  token,
  topicArn
}) => <div className="p-3 rounded-lg border border-[hsl(var(--border))] space-y-2" data-testid="ses-sns-section">
              <Label className="font-semibold">Bounce &amp; complaint feedback (Amazon SNS)</Label>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-xs">
                <span className={muted}>Endpoint for the SNS subscription:</span>
                <code className="px-2 py-1 rounded bg-[hsl(var(--muted))] break-all" data-testid="ses-sns-endpoint">{st.sns?.endpoint}</code>
                <Button variant="ghost" size="sm" onClick={copyEndpoint} data-testid="ses-sns-copy-button" aria-label="Copy endpoint"><Copy className="w-4 h-4" /></Button>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-start gap-2">
                <Button variant="outline" size="sm" onClick={checkEndpoint} disabled={busy} data-testid="ses-sns-endpoint-check-button">Check endpoint (redirects)</Button>
                <p className={`text-xs ${muted}`}>Amazon SNS does not follow redirects. The subscription must use the address that answers directly.</p>
              </div>
              {epCheck && <div className="text-xs space-y-1 p-2 rounded-md border border-[hsl(var(--border))]" data-testid="ses-sns-endpoint-check">
                  {epCheck.results.map(r => <div key={r.url} className="flex flex-wrap gap-2 items-center">
                      {r.ok ? <CheckCircle className="w-3.5 h-3.5 text-green-600" /> : <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />}
                      <code className="break-all">{r.url}</code>
                      <Badge variant="outline">{r.status ?? r.error ?? '—'}</Badge>
                      {r.redirect_to && <span className="text-amber-700">redirects to {r.redirect_to}</span>}
                    </div>)}
                  <p className={epCheck.recommended_endpoint ? 'font-medium' : 'text-amber-700'} data-testid="ses-sns-recommended">{epCheck.message}</p>
                </div>}
              <p className={`text-xs ${muted}`} data-testid="ses-sns-status">
                {st.sns?.approved_topic ? <>Approved topic: <code>{st.sns.approved_topic}</code>{st.sns.confirmed_at && <> · confirmed {fmt(st.sns.confirmed_at)}</>}</> : 'No topic approved yet. When AWS sends the subscription request it will appear below for your approval — nothing is accepted automatically.'}
              </p>
              <div className="grid sm:grid-cols-2 gap-2">
                <div><Label className="text-xs">Configuration set name (SES)</Label><Input value={cfgSet} onChange={e => setCfgSet(e.target.value)} placeholder="gsn-main" data-testid="ses-config-set-input" /></div>
                <div><Label className="text-xs">Approved SNS topic ARN (optional pre-approval)</Label><Input value={topicArn} onChange={e => setTopicArn(e.target.value)} placeholder="arn:aws:sns:eu-west-2:123456789012:gsn-ses-events" data-testid="ses-topic-arn-input" /></div>
              </div>
              <Button variant="outline" size="sm" onClick={saveSnsConfig} disabled={busy} data-testid="ses-sns-save-button">Save feedback settings</Button>
              {(st.sns?.pending || []).map(p => <div key={p.topic} className="flex flex-col sm:flex-row sm:items-center gap-2 p-2 rounded-md border border-amber-500/40 bg-amber-500/10 text-xs" data-testid="ses-sns-pending">
                  <span className="flex-1 break-all">Subscription request from <code>{p.topic}</code> ({fmt(p.received_at)})</span>
                  <Button size="sm" onClick={() => run(() => api.postAuth('/api/admin/email/ses/sns/approve', {
      topic: p.topic
    }, token))} disabled={busy} data-testid="ses-sns-approve-button">Approve</Button>
                  <Button size="sm" variant="outline" onClick={() => run(() => api.postAuth('/api/admin/email/ses/sns/reject', {
      topic: p.topic
    }, token), 'Ignored')} disabled={busy} data-testid="ses-sns-reject-button">Ignore</Button>
                </div>)}
            </div>;
